import classNames from 'classnames';
import React from 'react';

import {AssemblyPreview, AssemblyPreviewProps} from 'assembly/tabs/primer-assembly/AssemblyPreview';
import {DesignParameters, DesignParametersProps} from 'assembly/tabs/primer-assembly/DesignParameters';
import Mismatches, {MismatchAttrs} from 'assembly/tabs/primer-assembly/Mismatches';
import PrimerRows, {
  AssemblyAttrs,
  MaybeEnzyme,
  PrimerRowAttrs,
} from 'assembly/tabs/primer-assembly/PrimerRows';
import StickyEnds, {StickyEndsProps} from 'assembly/tabs/primer-assembly/StickyEnds';
import {ASSEMBLY_TYPE} from 'constants/AssemblyConstants';

export type MaybeAssemblyPreview =
  | {
      assemblyPreviewProps: AssemblyPreviewProps;
      showPrintableSummary: true;
    }
  | {assemblyPreviewProps?: never; showPrintableSummary: false};

export type MaybeMismatches =
  | {finalized: true; mismatches?: never}
  | {finalized: false; mismatches: Array<MismatchAttrs>};

export type PrimerAssemblyTabProps<TAssembly extends ASSEMBLY_TYPE.GIBSON | ASSEMBLY_TYPE.GOLDEN_GATE> = {
  designParametersProps: DesignParametersProps<TAssembly>;
  primerRows: Array<PrimerRowAttrs>;
  stickyEndsProps: StickyEndsProps | null;
} & AssemblyAttrs &
  MaybeAssemblyPreview &
  MaybeEnzyme &
  MaybeMismatches;

export default function PrimerAssemblyTab<
  TAssembly extends ASSEMBLY_TYPE.GIBSON | ASSEMBLY_TYPE.GOLDEN_GATE,
>({
  assemblyPreviewProps,
  designParametersProps,
  enzyme,
  isGoldenGate,
  finalized,
  mismatches,
  primerRows,
  showBinding,
  showHomology,
  showPrintableSummary,
  stickyEndsProps,
}: PrimerAssemblyTabProps<TAssembly>): JSX.Element {
  return (
    <form
      className={classNames({'printable-summary': showPrintableSummary}, 'buffer-content-container-fluid')}
    >
      {showPrintableSummary && (
        <AssemblyPreview
          fragmentSequenceNames={assemblyPreviewProps.fragmentSequenceNames}
          snapshotURL={assemblyPreviewProps.snapshotURL}
        />
      )}
      <PrimerRows
        {...(isGoldenGate
          ? {
              isGoldenGate,
              enzyme,
            }
          : {isGoldenGate})}
        finalized={finalized}
        primerRows={primerRows}
        showBinding={showBinding}
        showHomology={showHomology}
        showPrintableSummary={showPrintableSummary}
      />
      {stickyEndsProps != null && (
        <StickyEnds
          errors={stickyEndsProps.errors}
          finalized={finalized}
          rows={stickyEndsProps.rows}
          warnings={stickyEndsProps.warnings}
        />
      )}
      {!finalized && <Mismatches mismatches={mismatches} />}
      <DesignParameters
        assemblyType={designParametersProps.assemblyType}
        parameters={designParametersProps.parameters}
      />
    </form>
  );
}
